import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import Modal from '@/shared/components/Modal';
import { markFunnelStage } from '@/shared/analytics';
import { CreateAccountResponse } from '../../types';
import { continuationStep, type ConnectionStep } from '../continuation';
import CreationForm from './CreationModal/CreationForm';
import CreationConfirmation from './CreationModal/CreationConfirmation';
import PostConnectionActivation from './PostConnectionActivation';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onActivated?: () => void;
}

type Continuation = Extract<ConnectionStep, { name: 'activating' }>;

export default function CreationModal({ isOpen, onClose, onActivated }: Props) {
  const intl = useIntl();
  const [connection, setConnection] = useState<CreateAccountResponse | null>(null);
  const [continuation, setContinuation] = useState<Continuation | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    markFunnelStage('activation_modal_viewed', 'activation_modal_viewed');
  }, [isOpen]);

  const handleClose = () => {
    setConnection(null);
    setContinuation(null);
    markFunnelStage('activation_modal_closed', 'activation_modal_closed');
    onClose();
  };

  const handleActivated = () => {
    setConnection(null);
    setContinuation(null);
    markFunnelStage('activation_modal_completed', 'activation_modal_completed');
    onActivated?.();
    onClose();
  };

  const handleActivationDeclined = () => {
    setConnection(null);
    setContinuation(null);
    markFunnelStage('activation_modal_declined', 'activation_modal_declined');
    onClose();
  };

  const handleContinue = () => {
    if (!connection) return handleClose();

    const step = continuationStep(connection.account, connection.schedules);
    if (step.name !== 'activating') return handleClose();

    setContinuation(step);
  };

  if (continuation) {
    return (
      <PostConnectionActivation
        countryCode={continuation.account.country_code}
        schedule={continuation.schedule}
        onActivated={handleActivated}
        onCancelled={handleActivationDeclined}
      />
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={intl.formatMessage({ id: 'accounts.create.title' })}
      eyebrow={intl.formatMessage({ id: 'accounts.create.kicker' })}
    >
      {connection ? (
        <CreationConfirmation connection={connection} onContinue={handleContinue} />
      ) : (
        <CreationForm onConnected={setConnection} analyticsSource="dashboard" />
      )}
    </Modal>
  );
}
